import { Container, Sprite, Assets, Text, TextStyle } from 'pixi.js';
import CardAnimations from '../CardAnimations.js';
import { ANIMATION_DURATIONS } from '../../config/animationConfig.js';
import { toVisualSlot, toActualSeat, getDealingOrder } from './onlineSeat.js';
import ScoreMarkerRenderer from '../ScoreMarkerRenderer.js';
import OnlineDeckRenderer from './OnlineDeckRenderer.js';

/** Spanish deck size (same constant offline's Deck.js hardcodes) — the pile is shown full right before the first card is dealt off it. */
const FULL_DECK_SIZE = 40;

/**
 * Renders the online Ronda table — a FORK of src/game/DealManagerRenderer.js
 * (RONDA_ONLINE.md Fase 9b, per explicit instruction: replicate the offline
 * animations/rendering as a *base*, adapted, without sharing code with the
 * offline screen). Kept deliberately separate rather than imported, since
 * the two have genuinely incompatible data models:
 *
 * - Offline holds a live `DealManager` with all 4 hands in memory and reads
 *   from it on demand; this renderer is driven entirely by whatever the
 *   server's GAME_STATE_SYNC/CARD_PLAYED events say, and only ever knows
 *   opponents' hands as a face-down count.
 * - Offline hardcodes "the human is always seat 0" (src/game/seatUtils.js);
 *   here "my" seat can be 0-3, so every position lookup goes through a
 *   visual-slot remap (./onlineSeat.js) first — my seat always renders at
 *   the bottom (visual slot 0), everyone else rotated around it.
 *
 * What's ported near-verbatim (same math, same primitives): card texture
 * loading, the 4-seat/12-slot-center layout, animateCardDrop/
 * animateSequentialCollection/animateCardCollection, and the
 * interactivity helpers. Dealt cards animate out of the visual deck pile
 * (OnlineDeckRenderer, positioned at the current dealer's seat) exactly
 * like offline's DeckRenderer — dealInitial() plants it there and pops one
 * sprite per card dealt, via getDealOrigin().
 */
class OnlineTableRenderer {
    constructor(app, mySeat) {
        this.app = app;
        this.mySeat = mySeat;

        this.cardWidth = 100;
        this.cardHeight = 150;

        this.centerCardsContainer = null;
        /** Keyed by VISUAL SLOT (0 = bottom = me, 1 = right, 2 = top, 3 = left) — not by real seat. */
        this.playerCardsContainers = {};
        this.playerNameTexts = {};
        this.scoreMarkerRenderer = null;
        this.deckRenderer = new OnlineDeckRenderer(app);

        // Same center-slot fill order as offline: fills the middle 4 slots
        // first so the initial 4-card deal lands centered.
        this.slotFillOrder = [2, 3, 1, 4, 0, 5, 8, 9, 7, 10, 6, 11];

        this.initialize();
    }

    get dealDuration() {
        return ANIMATION_DURATIONS.DEAL_DURATION;
    }

    get dealDelay() {
        return ANIMATION_DURATIONS.DEAL_DELAY;
    }

    /** Offline parity (DealManagerRenderer.setCardSize) — call before setScoreMarkers/dealInitial so the deck/score sub-renderers pick it up the first time they read this.cardWidth/cardHeight. */
    setCardSize(width, height) {
        this.cardWidth = width;
        this.cardHeight = height;
    }

    initialize() {
        this.centerCardsContainer = new Container();
        this.centerCardsContainer.label = 'OnlineCenterCards';
        this.centerCardsContainer.zIndex = 100;
        this.app.stage.addChild(this.centerCardsContainer);

        for (let slot = 0; slot < 4; slot++) {
            const container = new Container();
            container.label = `OnlineSeatSlot${slot}Cards`;
            container.zIndex = 100;
            container.sortableChildren = true;
            this.app.stage.addChild(container);
            this.playerCardsContainers[slot] = container;
        }

        this.app.stage.sortableChildren = true;
    }

    // ─── Seat mapping ──────────────────────────────────────────────────

    visualSlot(actualSeat) {
        return toVisualSlot(actualSeat, this.mySeat);
    }

    // ─── Names ─────────────────────────────────────────────────────────

    /** `playersBySeat`: array of 4 `{name, isSystem, avatar?}`, indexed by REAL seat (0-3). */
    setPlayerNames(playersBySeat) {
        Object.values(this.playerNameTexts).forEach((item) => {
            if (item?.name && !item.name.destroyed) item.name.destroy();
            if (item?.avatar && !item.avatar.destroyed) item.avatar.destroy();
        });
        this.playerNameTexts = {};

        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;
        const margin = this.cardHeight * 0.6;

        const nameStyle = new TextStyle({
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            fontWeight: 'bold',
            fill: 0xffffff,
            stroke: { color: 0x000000, width: 3 },
            dropShadow: { color: 0x000000, blur: 4, angle: Math.PI / 4, distance: 2 },
        });
        const avatarStyle = new TextStyle({ fontFamily: 'Arial, sans-serif', fontSize: 22 });
        const avatarOffset = 35;

        for (let slot = 0; slot < 4; slot++) {
            const actualSeat = toActualSeat(slot, this.mySeat);
            const player = playersBySeat[actualSeat] ?? { name: '', isSystem: false };

            const nameText = new Text({ text: player.name, style: nameStyle });
            nameText.anchor.set(0.5);
            nameText.zIndex = 1;

            const avatarText = new Text({ text: player.avatar || (player.isSystem ? '🤖' : '🙂'), style: avatarStyle });
            avatarText.anchor.set(0.5);
            avatarText.zIndex = 1;

            switch (slot) {
                case 0:
                    nameText.x = centerX;
                    nameText.y = screenHeight - margin + 12;
                    avatarText.x = centerX;
                    avatarText.y = screenHeight - margin - avatarOffset + 12;
                    break;
                case 1:
                    nameText.x = screenWidth - margin + 12;
                    nameText.y = centerY;
                    avatarText.x = screenWidth - margin - avatarOffset + 12;
                    avatarText.y = centerY;
                    nameText.rotation = -Math.PI / 2;
                    avatarText.rotation = -Math.PI / 2;
                    break;
                case 2:
                    nameText.x = centerX;
                    nameText.y = margin - 12;
                    avatarText.x = centerX;
                    avatarText.y = margin + avatarOffset - 12;
                    break;
                case 3:
                    nameText.x = margin - 12;
                    nameText.y = centerY;
                    avatarText.x = margin + avatarOffset - 12;
                    avatarText.y = centerY;
                    nameText.rotation = Math.PI / 2;
                    avatarText.rotation = Math.PI / 2;
                    break;
                default:
                    break;
            }

            this.app.stage.addChild(avatarText);
            this.app.stage.addChild(nameText);
            this.playerNameTexts[slot] = { name: nameText, avatar: avatarText };
        }
    }

    // ─── Score marker (RONDA_ONLINE.md TODO #4) ─────────────────────────

    /** Mounts the same on-table score marker offline uses (src/game/ScoreMarkerRenderer.js), driven by the two ScoreMarker instances the screen owns. Called once, right after construction. */
    setScoreMarkers(team1Marker, team2Marker) {
        this.scoreMarkerRenderer = new ScoreMarkerRenderer(this.app, team1Marker, team2Marker);
        this.scoreMarkerRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.scoreMarkerRenderer.render();
    }

    /** Refreshes the marker's visuals after its ScoreMarker instances' scores changed. */
    updateScoreMarkers() {
        this.scoreMarkerRenderer?.update();
    }

    // ─── Deck pile (visual only — see OnlineDeckRenderer's doc comment) ──

    /** Repositions the deck pile next to `dealerSeat` and resizes its visible stack to `remaining` cards — call on every GAME_STATE_SYNC (dealerSeat/remaining can both change: a re-deal, a fresh shuffled deck, a round transition to a rotated dealer). */
    async updateDeck(dealerSeat, remaining) {
        this.deckRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.deckRenderer.setPositionByVisualSlot(this.visualSlot(dealerSeat), this.app.screen.width, this.app.screen.height);
        await this.deckRenderer.setCount(remaining);
    }

    // ─── Layout (identical math to DealManagerRenderer, indexed by visual slot) ──

    getCenterCardPosition(slotIndex) {
        if (!this.app?.renderer || !this.app?.screen) return { x: 0, y: 0, slotIndex: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        const slotsPerRow = 6;
        const spacing = this.cardWidth + 10;
        const rowSpacing = this.cardHeight + 15;

        const row = Math.floor(slotIndex / slotsPerRow);
        const posInRow = slotIndex % slotsPerRow;

        const totalRowWidth = slotsPerRow * this.cardWidth + (slotsPerRow - 1) * 10;
        const startX = centerX - totalRowWidth / 2 + this.cardWidth / 2;
        const x = startX + posInRow * spacing;
        const y = row === 0 ? centerY - rowSpacing / 2 : centerY + rowSpacing / 2;

        return { x, y, slotIndex };
    }

    getNextAvailableSlot() {
        const occupiedSlots = this.centerCardsContainer.children.map((sprite) => sprite.slotIndex ?? 0);
        for (const slot of this.slotFillOrder) {
            if (!occupiedSlots.includes(slot)) return slot;
        }
        return 11;
    }

    /** `visualSlot` here, unlike offline's `playerIndex`, is already rotated so mine is always 0. */
    getPlayerCardPosition(visualSlot, cardIndex) {
        if (!this.app?.renderer || !this.app?.screen) return { x: 0, y: 0, rotation: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        const spacing = this.cardWidth * 0.4;
        const margin = this.cardHeight * 0.6;

        switch (visualSlot) {
            case 0: {
                const fanRadius = this.cardWidth * 4;
                const fanAngle = 0.25;
                const cardAngle = (cardIndex - 1) * fanAngle;
                const arcX = centerX + Math.sin(cardAngle) * fanRadius;
                const arcY = screenHeight - margin + (1 - Math.cos(cardAngle)) * this.cardHeight * 0.3;
                return { x: arcX, y: arcY, rotation: cardAngle };
            }
            case 1: {
                const totalHeight = 2 * spacing;
                const startY = centerY - totalHeight / 2;
                return { x: screenWidth - margin, y: startY + cardIndex * spacing, rotation: -Math.PI / 2 };
            }
            case 2: {
                const totalWidth = 2 * spacing;
                const startX = centerX + totalWidth / 2;
                return { x: startX - cardIndex * spacing, y: margin, rotation: Math.PI };
            }
            case 3: {
                const totalHeight = 2 * spacing;
                const startY = centerY + totalHeight / 2;
                return { x: margin, y: startY - cardIndex * spacing, rotation: Math.PI / 2 };
            }
            default:
                return { x: 0, y: 0, rotation: 0 };
        }
    }

    /** Where a seat's collected cards fly to and vanish — same per-visual-slot corners as offline (Ronda.jsx's own pilePos switch), since DealManagerRenderer never owned this itself either. */
    getPilePosition(actualSeat) {
        if (!this.app?.renderer || !this.app?.screen) return { x: 0, y: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const pileMargin = 80;

        switch (this.visualSlot(actualSeat)) {
            case 0:
                return { x: screenWidth / 2 + this.cardWidth * 2, y: screenHeight - pileMargin };
            case 1:
                return { x: screenWidth - pileMargin, y: screenHeight / 2 + this.cardHeight };
            case 2:
                return { x: screenWidth / 2 - this.cardWidth * 2, y: pileMargin };
            case 3:
                return { x: pileMargin, y: screenHeight / 2 - this.cardHeight };
            default:
                return { x: screenWidth / 2, y: screenHeight / 2 };
        }
    }

    /** Where dealt cards fly out from — the deck pile's own position/rotation, wherever it's currently planted (dealInitial positions it at the dealer's seat first). */
    getDealOrigin() {
        return this.deckRenderer.getPosition();
    }

    // ─── Card sprites ──────────────────────────────────────────────────

    getCardImagePath(suit, value) {
        const suitMap = { oros: 1, copas: 2, espadas: 3, bastos: 4 };
        const suitCode = suitMap[suit];
        const valueCode = value.toString().padStart(2, '0');
        return `assets/images/deck/${suitCode}${valueCode}.png`;
    }

    getCardBackPath() {
        const cardBack = localStorage.getItem('cardBack') || '001';
        return `assets/images/deck/${cardBack}.png`;
    }

    /** Face-down placeholders (opponents' unrevealed hand cards) get a null-data sentinel — `card` is filled in for real once a CARD_PLAYED event names it. */
    async createCardSprite(card, faceUp) {
        const imagePath = faceUp ? this.getCardImagePath(card.suit, card.value) : this.getCardBackPath();
        const texture = await Assets.load(imagePath);
        const sprite = new Sprite(texture);
        sprite.width = this.cardWidth;
        sprite.height = this.cardHeight;
        sprite.anchor.set(0.5);
        sprite.cardData = card;
        sprite.isFaceUp = faceUp;
        return sprite;
    }

    // ─── Dealing ───────────────────────────────────────────────────────

    /**
     * Animates the very first GAME_STATE_SYNC into the initial deal: my real
     * hand face-up, opponents' hands as face-down placeholders (one per
     * `otherHandCounts` entry — Ronda always deals exactly 3, so this is
     * always 3 per opponent at this point), and the 4 center cards.
     * `payload` is the raw GAME_STATE_SYNC payload — same shape the server
     * already sends, no separate mapping needed.
     */
    async dealInitial(payload) {
        const { myHand, otherHandCounts, centerCards, dealerIndex } = payload;

        // Clears any stale sprites left over from the previous deck-round
        // (e.g. center cards nobody's play matched, silently swept to the
        // last collector server-side with no dedicated client animation) —
        // a no-op on the very first deal, when the stage is already empty.
        this.clearAllCards();

        // Plant the (already-shuffled) deck pile at the dealer's seat, full,
        // before pulling the first card off it — same as offline's
        // DeckRenderer.render() ahead of DealManagerRenderer.animateDealFirstHand.
        this.deckRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.deckRenderer.setPositionByVisualSlot(this.visualSlot(dealerIndex), this.app.screen.width, this.app.screen.height);
        await this.deckRenderer.render(FULL_DECK_SIZE);
        const origin = this.getDealOrigin();

        const handSprites = { [this.mySeat]: [] };
        for (const card of myHand) {
            handSprites[this.mySeat].push(await this.createCardSprite(card, true));
        }
        for (const [seatStr, count] of Object.entries(otherHandCounts)) {
            const seat = Number(seatStr);
            handSprites[seat] = [];
            for (let i = 0; i < count; i++) {
                handSprites[seat].push(await this.createCardSprite({ suit: null, value: null }, false));
            }
        }

        // Same interleaved sequence as offline's DealManager.dealFirstHand:
        // one center card, then one card to each seat in the real
        // dealer-relative order (dealer+1 .. dealer), repeated 3x, then a
        // final 4th center card — not "all centers first" and not the
        // viewer-relative order this used to deal in.
        const dealingOrder = getDealingOrder(dealerIndex);
        let centerIndex = 0;
        const dealNextCenterCard = async () => {
            const card = centerCards[centerIndex];
            centerIndex++;
            if (!card) return;
            const sprite = await this.createCardSprite(card, true);
            sprite.x = origin.x;
            sprite.y = origin.y;
            sprite.rotation = origin.rotation;
            this.centerCardsContainer.addChild(sprite);

            const slotIndex = this.getNextAvailableSlot();
            const targetPos = this.getCenterCardPosition(slotIndex);
            sprite.slotIndex = slotIndex;

            await Promise.all([
                CardAnimations.slideCard(sprite, targetPos, this.dealDuration),
                this.animateRotation(sprite, 0, this.dealDuration),
            ]);
            this.deckRenderer.removeTopCard();
            await this.delay(this.dealDelay);
        };

        for (let round = 0; round < 3; round++) {
            await dealNextCenterCard();
            for (const seat of dealingOrder) {
                const sprite = handSprites[seat]?.[round];
                if (!sprite) continue;
                const visualSlot = this.visualSlot(seat);

                sprite.x = origin.x;
                sprite.y = origin.y;
                sprite.rotation = origin.rotation;
                this.playerCardsContainers[visualSlot].addChild(sprite);

                const targetPos = this.getPlayerCardPosition(visualSlot, round);
                await Promise.all([
                    CardAnimations.slideCard(sprite, targetPos, this.dealDuration),
                    this.animateRotation(sprite, targetPos.rotation, this.dealDuration),
                ]);
                this.deckRenderer.removeTopCard();
                await this.delay(this.dealDelay);
            }
        }
        await dealNextCenterCard();
    }

    /**
     * Animates a same-deck internal re-deal within a Ronda "round" — one of
     * the (up to) 3 hands sharing a single 40-card deck before it runs out
     * and gets reshuffled (offline: DealManager.dealHand(dealer, false) +
     * DealManagerRenderer.animateDealSubsequentHand). Only the 12 player
     * cards are dealt — the round's center cards stay exactly where they
     * are, carried over from the hand that just finished, and the dealer
     * doesn't change either. `payload` is the raw GAME_STATE_SYNC that
     * already reflects the freshly-dealt hand (`deckRemaining` is
     * post-deal), so the deck pile is rebuilt one hand's worth (12 cards)
     * fuller than that before pulling cards back off it, same trick
     * `dealInitial` plays relative to `FULL_DECK_SIZE`.
     */
    async animateDealSubsequentHand(payload) {
        const { myHand, otherHandCounts, dealerIndex, deckRemaining } = payload;

        this.clearPlayerCards();

        this.deckRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.deckRenderer.setPositionByVisualSlot(this.visualSlot(dealerIndex), this.app.screen.width, this.app.screen.height);
        await this.deckRenderer.render(deckRemaining + 12);
        const origin = this.getDealOrigin();

        const handSprites = { [this.mySeat]: [] };
        for (const card of myHand) {
            handSprites[this.mySeat].push(await this.createCardSprite(card, true));
        }
        for (const [seatStr, count] of Object.entries(otherHandCounts)) {
            const seat = Number(seatStr);
            handSprites[seat] = [];
            for (let i = 0; i < count; i++) {
                handSprites[seat].push(await this.createCardSprite({ suit: null, value: null }, false));
            }
        }

        const dealingOrder = getDealingOrder(dealerIndex);
        for (let round = 0; round < 3; round++) {
            for (const seat of dealingOrder) {
                const sprite = handSprites[seat]?.[round];
                if (!sprite) continue;
                const visualSlot = this.visualSlot(seat);

                sprite.x = origin.x;
                sprite.y = origin.y;
                sprite.rotation = origin.rotation;
                this.playerCardsContainers[visualSlot].addChild(sprite);

                const targetPos = this.getPlayerCardPosition(visualSlot, round);
                await Promise.all([
                    CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
                    this.animateRotation(sprite, targetPos.rotation, this.dealDuration),
                ]);
                this.deckRenderer.removeTopCard();
                await this.delay(this.dealDelay);
            }
        }
    }

    /**
     * Instantly snaps the whole table to whatever a GAME_STATE_SYNC says —
     * no deal animation. Used after a reconnection (Fase 9c): the server
     * always sends a fresh sync right after re-attaching the socket, but it
     * has no CARD_PLAYED before it (nothing was "just played"), so the
     * normal incremental path in OnlineRondaScreen has nothing to animate
     * off of. Re-running dealInitial's flourish every time you reconnect
     * mid-hand would look wrong — this rebuilds the board directly in its
     * final positions instead.
     */
    async renderState(payload) {
        const { myHand, otherHandCounts, centerCards } = payload;
        this.clearAllCards();

        for (const card of centerCards) {
            const sprite = await this.createCardSprite(card, true);
            const slotIndex = this.getNextAvailableSlot();
            const pos = this.getCenterCardPosition(slotIndex);
            sprite.x = pos.x;
            sprite.y = pos.y;
            sprite.slotIndex = slotIndex;
            this.centerCardsContainer.addChild(sprite);
        }

        for (let i = 0; i < myHand.length; i++) {
            const sprite = await this.createCardSprite(myHand[i], true);
            const pos = this.getPlayerCardPosition(0, i); // visual slot 0 = me, always
            sprite.x = pos.x;
            sprite.y = pos.y;
            sprite.rotation = pos.rotation;
            this.playerCardsContainers[0].addChild(sprite);
        }

        for (const [seatStr, count] of Object.entries(otherHandCounts)) {
            const seat = Number(seatStr);
            const visualSlot = this.visualSlot(seat);
            for (let i = 0; i < count; i++) {
                const sprite = await this.createCardSprite({ suit: null, value: null }, false);
                const pos = this.getPlayerCardPosition(visualSlot, i);
                sprite.x = pos.x;
                sprite.y = pos.y;
                sprite.rotation = pos.rotation;
                this.playerCardsContainers[visualSlot].addChild(sprite);
            }
        }
    }

    // ─── Play animations (ported ~verbatim from DealManagerRenderer) ────

    /** Flips a face-down placeholder (opponent hand card) to its real face once its `cardData` is known — a no-op if it's already face up. Shared by every path that can move a just-played opponent card, whether it drops into an empty slot or immediately collects a match. */
    async revealCardSprite(cardSprite) {
        if (cardSprite.isFaceUp) return;
        const texture = await Assets.load(this.getCardImagePath(cardSprite.cardData.suit, cardSprite.cardData.value));
        cardSprite.texture = texture;
        cardSprite.isFaceUp = true;
    }

    /** `actualSeat` is a real server seat (0-3) — remapped to a visual slot internally. */
    async animateCardDrop(actualSeat, cardSprite) {
        const visualSlot = this.visualSlot(actualSeat);
        const worldPos = cardSprite.toGlobal({ x: 0, y: 0 });
        const centerContainerWorldPos = this.centerCardsContainer.toGlobal({ x: 0, y: 0 });

        const playerContainer = this.playerCardsContainers[visualSlot];
        if (playerContainer && cardSprite.parent === playerContainer) {
            playerContainer.removeChild(cardSprite);
        }
        this.centerCardsContainer.addChild(cardSprite);

        cardSprite.x = worldPos.x - centerContainerWorldPos.x;
        cardSprite.y = worldPos.y - centerContainerWorldPos.y;

        await this.revealCardSprite(cardSprite);

        const matchingCardSprite = this.centerCardsContainer.children.find(
            (child) => child !== cardSprite && child.cardData?.value === cardSprite.cardData.value
        );

        let targetPos;
        let targetRotation = 0;
        if (matchingCardSprite) {
            const randomOffsetX = (Math.random() - 0.5) * 30;
            const randomOffsetY = (Math.random() - 0.5) * 30;
            targetRotation = (Math.random() - 0.5) * 0.4;
            targetPos = {
                x: matchingCardSprite.x + randomOffsetX,
                y: matchingCardSprite.y + randomOffsetY,
                slotIndex: matchingCardSprite.slotIndex,
            };
            cardSprite.slotIndex = matchingCardSprite.slotIndex;
        } else {
            const slotIndex = this.getNextAvailableSlot();
            targetPos = this.getCenterCardPosition(slotIndex);
            cardSprite.slotIndex = targetPos.slotIndex;
        }

        await Promise.all([
            CardAnimations.slideCard(cardSprite, targetPos, this.dealDuration),
            this.animateRotation(cardSprite, targetRotation, this.dealDuration),
        ]);
    }

    async animateCardCollection(cardSprites, targetPos) {
        await Promise.all(
            cardSprites.map((sprite) =>
                Promise.all([
                    CardAnimations.slideCard(sprite, targetPos, this.dealDuration),
                    this.animateRotation(sprite, 0, this.dealDuration),
                ])
            )
        );
    }

    async animateSequentialCollection(playedCardSprite, matchedCards, pilePos) {
        if (matchedCards.length === 0) return;

        // The played card can still be an unrevealed opponent placeholder
        // here (a play that collects on its very first move, never having
        // gone through animateCardDrop) — flip it before it starts sliding
        // so it never rides into the pile showing its back.
        await this.revealCardSprite(playedCardSprite);

        const firstMatch = matchedCards[0];
        await this.animateCardCollection([playedCardSprite], { x: firstMatch.sprite.x, y: firstMatch.sprite.y });

        for (let i = 1; i < matchedCards.length; i++) {
            const currentSprite = matchedCards[i].sprite;
            const previousSprite = matchedCards[i - 1].sprite;
            await this.animateCardCollection([currentSprite], { x: previousSprite.x, y: previousSprite.y });
        }

        const allSprites = [playedCardSprite, ...matchedCards.map((m) => m.sprite)];
        await this.animateCardCollection(allSprites, pilePos);
    }

    animateRotation(sprite, targetRotation, duration) {
        return new Promise((resolve) => {
            const start = performance.now();
            const startRotation = sprite.rotation;
            let diff = targetRotation - startRotation;
            while (diff > Math.PI) diff -= 2 * Math.PI;
            while (diff < -Math.PI) diff += 2 * Math.PI;

            const animate = (time) => {
                if (sprite.destroyed) return;
                const t = Math.min(1, (time - start) / duration);
                const easedT = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
                sprite.rotation = startRotation + diff * easedT;
                if (t < 1) requestAnimationFrame(animate);
                else {
                    sprite.rotation = targetRotation;
                    resolve();
                }
            };
            requestAnimationFrame(animate);
        });
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    // ─── Interactivity (ported verbatim) ──────────────────────────────

    makePlayerCardsInteractive(actualSeat, callback) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (!container) return;

        container.children.forEach((sprite) => {
            if (!sprite._originalY) {
                sprite._originalY = sprite.y;
                sprite._originalScale = sprite.scale.x;
            }
            sprite.eventMode = 'static';
            sprite.cursor = 'pointer';
            sprite.removeAllListeners();

            sprite.on('pointerdown', () => callback(sprite.cardData, sprite));
            sprite.on('pointerover', () => {
                sprite.zIndex = 1000;
                sprite.scale.set(sprite._originalScale * 1.2);
                sprite.y = sprite._originalY - 20;
            });
            sprite.on('pointerout', () => {
                sprite.zIndex = 0;
                sprite.scale.set(sprite._originalScale);
                sprite.y = sprite._originalY;
            });
        });
    }

    disablePlayerCardsInteractivity(actualSeat) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (!container) return;
        container.children.forEach((sprite) => {
            sprite.eventMode = 'none';
            sprite.cursor = 'default';
            sprite.removeAllListeners();
        });
    }

    /** Minimal visual cue for a disconnected/substituted seat (Fase 9b baseline — richer reconnection UX is Fase 9c) — dims that seat's card pile. */
    setSeatDimmed(actualSeat, dimmed) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (container) container.alpha = dimmed ? 0.4 : 1;
    }

    // ─── Reads ─────────────────────────────────────────────────────────

    getCenterCardSprites() {
        return this.centerCardsContainer.children;
    }

    getPlayerCardSprites(actualSeat) {
        return this.playerCardsContainers[this.visualSlot(actualSeat)]?.children ?? [];
    }

    // ─── Cleanup ───────────────────────────────────────────────────────

    clearPlayerCards() {
        for (let slot = 0; slot < 4; slot++) {
            this.playerCardsContainers[slot].children.forEach((sprite) => sprite.destroy());
            this.playerCardsContainers[slot].removeChildren();
        }
    }

    clearAllCards() {
        this.centerCardsContainer.children.forEach((sprite) => sprite.destroy());
        this.centerCardsContainer.removeChildren();
        this.clearPlayerCards();
    }

    destroy() {
        CardAnimations.cancelAllAnimations();
        this.clearAllCards();
        this.deckRenderer.destroy();

        Object.values(this.playerNameTexts).forEach((item) => {
            if (item?.name) {
                if (item.name.parent === this.app.stage) this.app.stage.removeChild(item.name);
                if (!item.name.destroyed) item.name.destroy();
            }
            if (item?.avatar) {
                if (item.avatar.parent === this.app.stage) this.app.stage.removeChild(item.avatar);
                if (!item.avatar.destroyed) item.avatar.destroy();
            }
        });
        this.playerNameTexts = {};

        if (this.centerCardsContainer) {
            if (this.centerCardsContainer.parent === this.app.stage) this.app.stage.removeChild(this.centerCardsContainer);
            if (!this.centerCardsContainer.destroyed) this.centerCardsContainer.destroy();
        }

        Object.values(this.playerCardsContainers).forEach((container) => {
            if (container.parent === this.app.stage) this.app.stage.removeChild(container);
            if (!container.destroyed) container.destroy();
        });
        this.playerCardsContainers = {};
    }
}

export default OnlineTableRenderer;
